/**
 * Wordings of «Учитель» v3 — family «mistakes» (docs/TEACHING-V3.md §5; pool specs: ../spec.ts).
 * Rules: no Latin, NO squares (the board shows them), Гамбитик speaks of himself as a boy, the child through {g:…},
 * no «молодец», no clock words; «вот эти клетки / этот фланг / сюда» only in pools with a board cue.
 *
 * What these lines are for (§2.8): right after the child's move, while the bot thinks — what happened, by a concept
 * (`v3.mistake.*`, without squares and without blame), the rule to remember (`v3.rule.*`, first time in a game) or the
 * same rule as a question (`v3.rule.ask.*`, stages 3–5, second time); the take-back offer (`v3.takeback.*`):
 * stages 1–2 «Стоп-стоп! {что случилось}. Вернём ход?», stages 3–5 «Стоп! Что теперь может съесть соперник?».
 * The opponent has not answered yet: the mistake lines speak of what he CAN do now («соперник может…»).
 * A mistake line explains the consequence of OUR move; the alarms («Осторожно!», «Берегись!») belong to `v3.danger.*`.
 * Stages 3–5 hear a mistake line again after the take-back reply (reaction.ts), so the older and shared wordings avoid
 * «теперь» and «ход сделан» where they can (after «Верну» the move is no longer on the board).
 * Openers (the ear hears the first words; a game also hears `v3.danger.*`, which own «Соперник может съесть…»):
 * no mistake wording opens with «Соперник может съесть»; per pool at most 2 wordings open with «Соперник может»,
 * at most 2 with «{Конь} соперника», and per band at most 2 with «{Твой} {конь}» / «{Твоего} {коня}».
 * The take-back replies: `yes` / `no` stand alone at stages 1–2 and are followed by a mistake line at 3–5; `again`
 * opens the second offer — at stages 1–2 a mistake line and «Вернём ход?» follow, at 3–5 an `askThink` question, so
 * `again` is a statement, never a question.
 * Never: «ошибка», «зевок», «ты не…», «забыл», «зря», «опять», «снова», «неверно», «молодец», «так держать».
 *
 * Chess truth the words keep to (core coach/lesson/mistake.ts, analysis/motifs.ts):
 *  - fork: the forker is never the king, the targets are pieces or the king (never pawns);
 *  - pin / trapped: the victim is a knight, bishop, rook or queen; a pin is to the king OR to a dearer piece, so the
 *    words say «уходить нельзя / трудно / опасно», never «не сможет уйти» as a law;
 *  - backRank: the king's front squares are blocked by his own men — not necessarily pawns;
 *  - hanging.cheaper: the victim is defended, a cheaper piece attacks it; outnumbered: more attackers than defenders;
 *  - ignoredDanger: the warned piece stayed where it was — it may have got a defender that was not enough;
 *  - missedTreasure: the gift may be a mate or a fork (no piece) — piece-free wordings exist; never «бесплатно»;
 *  - slower: the engine does not prove «без потерь», so no wording claims it.
 */
import type { LessonWordings } from '../types.ts';

/** the younger band (stages 1–2) and the older band (3–5) of a banded pool */
const Y = [1, 2] as const;
const O = [3, 5] as const;

export const WORDINGS: LessonWordings = {
  // ───────────────────────── what happened (a concept, no blame) ─────────────────────────

  // a forced mate in 1–3 after this move
  'v3.mistake.mate': [
    { t: 'Ой, теперь соперник может поставить мат!', stages: Y },
    { t: 'Наш король в опасности — у соперника есть мат.', stages: Y },
    { t: 'Так соперник доберётся до нашего короля, и будет мат.', stages: Y },
    { t: 'Соперник может объявить мат нашему королю.', stages: Y },
    { t: 'Нашему королю теперь не убежать от мата.', stages: Y },
    { t: 'Эх, соперник теперь может поймать нашего короля.', stages: Y },
    { t: 'Этот ход открыл сопернику дорогу к мату.', stages: O },
    { t: 'У соперника появилась матовая атака на нашего короля.', stages: O },
    { t: 'Нашему королю не хватает защиты — соперник может поставить мат.', stages: O },
    { t: 'Соперник может закончить партию матом.', stages: O },
    { t: 'Матовая сеть готова — сопернику осталось её затянуть.', stages: O },
    { t: 'У соперника есть мат — наш король не успеет спрятаться.', stages: O },
    { t: 'После этого хода королю грозит мат.' },
    { t: 'Этот ход пускает соперника к нашему королю — дело идёт к мату.' },
    { t: 'Наш король попал в беду: мат совсем близко.' },
  ],

  // the king's front squares are blocked by his own men (pawns or pieces); «последняя горизонталь» from stage 4
  'v3.mistake.backRank': [
    { t: 'Король заперт своими же фигурами — по последнему ряду грозит мат.' },
    { t: 'Короля не выпускают свои же соседи, а соперник целится в последний ряд.' },
    { t: 'Король сидит как в запертом домике — соперник может дать мат.' },
    { t: 'Последний ряд остался без охраны — там соперник может поставить мат.' },
    { t: 'Без форточки королю не спастись от мата по последнему ряду.' },
    { t: 'Королю некуда шагнуть: соперник может дать шах по последнему ряду, и это мат.' },
    { t: 'Соперник может пробраться на последний ряд, а король там заперт.' },
    { t: 'Последняя горизонталь стала опасной: король заперт, и соперник может дать мат.', stages: [4, 5] },
    { t: 'По последней горизонтали соперник может поставить мат — выхода у короля нет.', stages: [4, 5] },
  ],

  // attacker = the opponent's forking piece (never the king); «вилка» from stage 2, «двойной удар» before
  'v3.mistake.fork': [
    { t: '{Конь} соперника может напасть сразу на две наши фигуры.', stages: Y },
    { t: 'Две наши фигуры стоят неудачно: {конь} соперника может атаковать обе.', stages: Y },
    { t: 'Одним ходом {конь} соперника может ударить по двум нашим фигурам.', stages: Y },
    { t: 'Смотри: {конь} соперника может напасть на две фигуры, а спасём мы только одну.', stages: Y },
    { t: 'У {коня:gen} соперника появился двойной удар по нашим фигурам.', stages: Y },
    { t: 'Соперник может сделать вилку: {конь} нападёт сразу на две фигуры.', stages: [2, 2] },
    { t: 'Появилась вилка: {конь} соперника может напасть на две фигуры сразу.', stages: O },
    { t: '{Конём} соперник может сделать вилку — одна из фигур пропадёт.', stages: O },
    { t: 'Наши фигуры стоят так, что {конь} соперника может взять их на вилку.', stages: O },
    { t: 'После этого хода у {коня:gen} соперника появляется вилка.', stages: O },
    { t: 'Вилка {коня:gen} соперника будет стоить нам фигуры.', stages: O },
    { t: '{Конь} соперника может ударить в две стороны — одну фигуру мы потеряем.' },
    { t: 'Один ход {конём} — и у соперника под ударом сразу две наши фигуры.' },
    { t: 'Найдётся ход, которым {конь} соперника ударит сразу по двум нашим фигурам.' },
    // piece-free: the engine may not know the forking piece
    { t: 'Под один удар могут попасть сразу две наши фигуры.', stages: Y },
    { t: 'У соперника появилась вилка: две наши фигуры под одним ударом.', stages: O },
    { t: 'Одна фигура соперника может напасть сразу на двух наших.' },
  ],

  // victim = our piece that gets pinned (knight, bishop, rook or queen) to the king or to a dearer piece; «связка» from stage 3
  'v3.mistake.pin': [
    { t: 'Соперник может прижать {твоего} {коня} к фигуре поважнее.' },
    { t: '{Твой} {конь} закрывает линию к фигуре поважнее — уходить {ему} нельзя.' },
    { t: 'Фигура соперника смотрит сквозь {твоего} {коня} на ту, что за {p:ним|ней}.' },
    { t: 'Позади {коня:gen} стоит фигура поважнее — соперник может этим воспользоваться.' },
    { t: 'Как бы {твой} {конь} не {p:прилип|прилипла} к месту: за {p:ним|ней} стоит кто-то важнее.' },
    { t: 'Если {твой} {конь} уйдёт, откроется дорога к кому-то важнее.' },
    { t: 'Соперник может связать {твоего} {коня}.', stages: O },
    { t: 'Грозит связка — {твой} {конь} застрянет на линии.', stages: O },
    { t: 'Шаг соперника — и {твой} {конь} в связке: сдвинуться {ему} будет трудно.', stages: O },
    // piece-free: the engine may not know the pinned piece
    { t: 'Наша фигура может застрять на линии — за ней стоит кто-то важнее.' },
    { t: 'Одна наша фигура закрывает собой другую, поважнее, — соперник это видит.' },
    { t: 'Связка на подходе: наша фигура не сможет спокойно уйти с линии.', stages: O },
  ],

  // the front piece must step aside (the king or a dearer piece), the back one is taken
  'v3.mistake.skewer': [
    { t: 'Соперник может сделать сквозной удар: передняя фигура уйдёт — пропадёт задняя.' },
    { t: 'Две наши фигуры стоят на одной линии — соперник может ударить сквозь них.' },
    { t: 'Грозит сквозной удар: нападут на дорогую фигуру, а заберут ту, что за ней.' },
    { t: 'Наши фигуры выстроились друг за другом — удобная цель для сквозного удара.' },
    { t: 'Передней фигуре придётся отойти, и соперник заберёт ту, что сзади.' },
    { t: 'У соперника наготове сквозной удар.' },
  ],

  // «вскрытое нападение» is a stage-4 word: stage 3 hears it described
  'v3.mistake.discovered': [
    { t: 'Фигура соперника может отойти и открыть дорогу другой — та нападёт.' },
    { t: 'Одна фигура соперника прячет за собой другую: стоит ей отойти — будет нападение.' },
    { t: 'Соперник может убрать фигуру с линии, и откроется удар из-за спины.' },
    { t: 'Соперник может пойти одной фигурой, а напасть другой.' },
    { t: 'Засада: за фигурой соперника прячется ещё одна, и она ударит.' },
    { t: 'Грозит вскрытое нападение — ход одной фигурой откроет удар другой.', stages: [4, 5] },
    { t: 'Вскрытое нападение: одна фигура соперника уйдёт, и откроется другая.', stages: [4, 5] },
  ],

  // capture the guard, it is taken back, then the prize goes; «защитник» is a stage-4 word: stage 3 hears «охрана», «сторож»
  'v3.mistake.removeDefender': [
    { t: 'Если соперник съест нашего сторожа, другая фигура окажется в опасности.' },
    { t: 'Одну нашу фигуру охраняет другая — соперник может сначала забрать охрану.' },
    { t: 'Сначала соперник уберёт охрану, а потом доберётся до фигуры.' },
    { t: 'Нашего сторожа могут разменять, а потом забрать то, что он берёг.' },
    { t: 'Наша охрана под прицелом: без неё соперник заберёт фигуру.' },
    { t: 'Соперник может убрать защитника — и наша фигура пропадёт.', stages: [4, 5] },
    { t: 'Соперник готовит приём: убрать защитника и забрать фигуру.', stages: [4, 5] },
  ],

  // victim = a knight, bishop, rook or queen whose every escape loses (the opponent's next move shuts it in)
  'v3.mistake.trapped': [
    { t: '{Твой} {конь} может попасть в ловушку — уйти будет некуда.' },
    { t: 'Соперник может поймать {твоего} {коня}: все выходы закрыты.' },
    { t: '{Твой} {конь} стоит в тупике — соперник может {его} окружить.' },
    { t: 'Соперник может запереть {твоего} {коня}, как в коробочке.' },
    { t: 'Ещё чуть-чуть — и {твой} {конь} окажется в ловушке.' },
    { t: 'У {коня:gen} не осталось безопасных клеток — {его} можно поймать.' },
    // piece-free: the engine may not know the trapped piece
    { t: 'Нашей фигуре скоро будет некуда уйти — её могут поймать.' },
    { t: 'Одна наша фигура может попасть в ловушку.' },
  ],

  // the piece is attacked and NOBODY defends it — «без защиты», «бесплатно» are true here
  'v3.mistake.hanging.undefended': [
    { t: '{Твой} {конь} теперь без защиты — соперник может съесть {его} бесплатно.', stages: Y },
    { t: 'Ой, {твоего} {коня} никто не защищает, а соперник может {его} съесть!', stages: Y },
    { t: '{p:Наш|Наша} {конь} стоит совсем {p:один|одна} — соперник может {его} забрать.', stages: Y },
    { t: 'Под ударом {твой} {конь}, и защитить {его} некому.', stages: Y },
    { t: 'Соперник может бесплатно забрать {твоего} {коня}.', stages: Y },
    { t: 'Никто не прикрывает {твоего} {коня} — это подарок для соперника.', stages: Y },
    { t: 'Смотри: {твой} {конь} {p:остался|осталась} без охраны, и {его} можно съесть.', stages: Y },
    { t: '{Твой} {конь} висит — так говорят, когда фигура под ударом без защиты.', stages: O },
    { t: 'После такого хода {твой} {конь} остаётся под ударом без защиты.', stages: O },
    { t: 'Соперник может взять {твоего} {коня} даром — защиты нет.', stages: O },
    { t: '{Твоего} {коня} атакуют, а защищать {его} некому.', stages: O },
    { t: 'У соперника появилось бесплатное взятие: {твой} {конь} без защиты.', stages: O },
    { t: 'Никто не охраняет {твоего} {коня}, и соперник это видит.', stages: O },
    { t: 'Посчитаем, кто защищает {твоего} {коня}: никто.', stages: O },
    { t: '{Твой} {конь} под ударом, и {его} никто не защищает.' },
    { t: 'Если соперник съест {твоего} {коня}, отбить будет нечем.' },
    { t: 'Эх, {твой} {конь} без защиты — соперник может {его} взять.' },
    { t: 'За {твоего} {коня} некому заступиться.' },
    { t: 'Соперник облизывается: {твой} {конь} без защиты.' },
  ],

  // the REAL reason: a cheaper piece attacks it — even defended, the trade loses (never a pawn: nothing is cheaper)
  'v3.mistake.hanging.cheaper': [
    { t: 'На {твоего} {коня} нападает фигура подешевле — защита не поможет.', stages: Y },
    { t: '{Твоего} {коня} может съесть фигура подешевле, и мы потеряем больше.', stages: Y },
    { t: 'Соперник может отдать фигуру подешевле, а забрать {твоего} {коня}.', stages: Y },
    { t: '{Твой} {конь} дороже фигуры, которая {его} атакует.', stages: Y },
    { t: 'Даже с защитой {твой} {конь} в опасности — {его} атакует фигура слабее.', stages: Y },
    { t: 'Дешёвая фигура соперника смотрит на {твоего} {коня} — меняться нам невыгодно.', stages: Y },
    { t: 'Маленькая фигура соперника охотится на {твоего} {коня} — отдавать {его} жалко.', stages: Y },
    { t: '{Твоего} {коня} атакует более дешёвая фигура — одной защиты мало.', stages: O },
    { t: 'Размен выйдет невыгодным: соперник отдаст дешёвую фигуру за {твоего} {коня}.', stages: O },
    { t: 'Под ударом {твой} {конь}, и бьёт {его} фигура подешевле.', stages: O },
    { t: 'Считаем цену: {конь} стоит больше, чем фигура, которая {его} атакует.', stages: O },
    { t: 'Нападает фигура дешевле, поэтому {твой} {конь} в опасности даже под защитой.', stages: O },
    { t: 'Обмен {коня:gen} на фигуру подешевле нам невыгоден.', stages: O },
    { t: 'Защищать мало: {твой} {конь} ценнее того, кто {его} атакует.', stages: O },
    { t: 'Глянь, кто нападает на {твоего} {коня}: фигура подешевле!' },
    { t: 'Фигура подешевле нападает на {твоего} {коня} — отдавать дорогое за дешёвое невыгодно.' },
    { t: '{Твой} {конь} стоит дороже нападающего — даже с защитой мы потеряем.' },
  ],

  // the REAL reason: more attackers than defenders («защитник» only from stage 4)
  'v3.mistake.hanging.outnumbered': [
    { t: 'На {твоего} {коня} нападает больше фигур, чем {его} защищает.' },
    { t: '{Твоего} {коня} защищают, но нападающих больше — {его} могут забрать.' },
    { t: 'У соперника больше сил против {коня:gen}, чем у нас в защите.' },
    { t: 'Посчитаем, кто нападает на {твоего} {коня} и кто защищает: нападающих больше.' },
    { t: 'Защиты не хватает: на {твоего} {коня} нападает слишком много фигур.' },
    { t: 'Соперник собрал против {коня:gen} целую команду — нам не отбиться.' },
    { t: 'Сил в защите меньше, чем в нападении, — {твой} {конь} под угрозой.' },
    { t: 'Нападающих больше, чем защитников, — {твой} {конь} не выстоит.', stages: [4, 5] },
  ],

  // the child took a cheaper piece with a dearer one and it can be taken back at a loss
  'v3.mistake.badTrade': [
    { t: 'Такой размен невыгодный: мы отдадим больше, чем забрали.', stages: Y },
    { t: 'Наша фигура дороже той, что мы съели, — соперник может съесть её в ответ.', stages: Y },
    { t: 'Мы съели фигуру подешевле, а отдадим подороже.', stages: Y },
    { t: 'Обмен неравный: наша фигура стоит больше.', stages: Y },
    { t: 'Так мы меняем дорогую фигуру на дешёвую — это невыгодно.', stages: Y },
    { t: 'Взяли мы меньше, чем потеряем.', stages: Y },
    { t: 'Размен в пользу соперника: наша фигура ценнее той, что мы взяли.', stages: O },
    { t: 'Посчитаем размен: отдаём больше, чем получаем.', stages: O },
    { t: 'Взятие вышло дорогим: в ответ соперник может забрать фигуру ценнее.', stages: O },
    { t: 'По цене фигур такой размен нам невыгоден.', stages: O },
    { t: 'Этот размен в минус: после ответного взятия у нас станет меньше сил.', stages: O },
    { t: 'Счёт не в нашу пользу: забрали меньше, чем отдадим.', stages: O },
    { t: 'Размен получается невыгодным — наша фигура дороже.' },
    { t: 'Такой размен нам не нужен: соперник получит больше, чем отдаст.' },
    { t: 'Соперник отобьёт, и мы останемся в минусе.' },
  ],

  // Гамбитик warned about this piece and it stayed where it was (maybe with a defender that is not enough);
  // no reproach, no «я же говорил», no «помощь не пришла»
  'v3.mistake.ignoredDanger': [
    { t: '{Твой} {конь} всё ещё под ударом.', stages: Y },
    { t: 'Угроза осталась: {твоего} {коня} можно съесть.', stages: Y },
    { t: 'А {конь} так и стоит под прицелом соперника.', stages: Y },
    { t: '{p:Бедный|Бедная} {конь} по-прежнему в беде!', stages: Y },
    { t: 'План соперника прежний — съесть {твоего} {коня}.', stages: Y },
    { t: '{Твой} {конь} всё так же в опасности — ход {его} не выручил.', stages: O },
    { t: 'Этот ход не убрал угрозу — {твоего} {коня} всё ещё можно взять.', stages: O },
    { t: 'Нападение на {твоего} {коня} осталось в силе.', stages: O },
    { t: 'Как и собирался, соперник может забрать {твоего} {коня}.', stages: O },
    { t: 'Опасность не прошла: {твой} {конь} по-прежнему под прицелом.', stages: O },
    { t: '{Твой} {конь} так и {p:остался|осталась} под ударом.' },
    { t: '{p:Наш|Наша} {конь} стоит на прежнем месте, и соперник может {его} взять.' },
    { t: 'Угроза никуда не делась — соперник целится в {твоего} {коня}.' },
    { t: 'Соперник помнит про {твоего} {коня} — и может {его} взять.' },
  ],

  // target = the opponent's piece that could be taken (free OR with profit — never claim «бесплатно»); null = a mate
  // or a fork was the gift, so piece-free wordings exist in every band
  'v3.mistake.missedTreasure': [
    { t: 'А ведь можно было съесть {коня} соперника!', stages: Y },
    { t: 'Тут был подарок — {конь} соперника!', stages: Y },
    { t: 'Мы прошли мимо подарка: можно было взять {коня} соперника.', stages: Y },
    { t: '{Конь} соперника так и {p:ждал|ждала}, чтобы {его} съели!', stages: Y },
    { t: 'Эх, {конь} соперника {p:был|была} для нас подарком!', stages: Y },
    { t: 'Был ход сильнее — взять {коня} соперника.', stages: O },
    { t: 'Мы упустили {коня} соперника — {его} можно было выгодно взять.', stages: O },
    { t: 'Можно было выиграть материал — съесть {коня} соперника.', stages: O },
    { t: '{Конь} соперника {p:стоял|стояла} под ударом — {его} можно было забрать с выгодой.', stages: O },
    { t: 'Взять {коня} соперника было выгодно — это был подарок.', stages: O },
    { t: 'Жаль, можно было выгодно съесть {коня} соперника.' },
    { t: 'Подарок был совсем рядом: {конь} соперника.' },
    { t: 'Ход назад можно было забрать {коня} соперника.' },
    // piece-free: the gift may be a mate or a fork, not a capture
    { t: 'Ой, мы пропустили подарок!', stages: Y },
    { t: 'Тут прятался подарок — в другой раз найдём!', stages: Y },
    { t: 'На доске прятался выгодный удар.', stages: O },
    { t: 'Жаль: был ход, который приносил выгоду.', stages: O },
    { t: 'Эх, подарок так и остался на доске.' },
  ],

  // the refutation really chases the queen; «ранний ферзь» is a stage-2 word: stage 1 hears «вышел слишком рано»
  'v3.mistake.earlyQueen': [
    { t: 'Ферзь вышел слишком рано — фигуры соперника будут его гонять.' },
    { t: 'Ферзю пока рано гулять — на него начнётся охота.' },
    { t: 'Наш ферзь вышел один, без помощников, — на него легко напасть.' },
    { t: 'Пока ферзь бегает от нападений, соперник выводит фигуры.' },
    { t: 'Ферзь впереди всех — удобная мишень для соперника.' },
    { t: 'Соперник будет выводить фигуры с нападением на нашего ферзя.' },
    { t: 'Это ранний ферзь — соперник рад за ним поохотиться.', stages: [2, 5] },
  ],

  // «проходная» is a stage-5 word
  'v3.mistake.promotion': [
    { t: 'Пешка соперника может дойти до конца и стать ферзём.' },
    { t: 'Соперник может превратить пешку в ферзя.' },
    { t: 'Путь пешке соперника открыт — впереди превращение.' },
    { t: 'Пешку соперника трудно остановить — она рвётся в ферзи.' },
    { t: 'После этого хода пешка соперника прорывается к последнему ряду.' },
    { t: 'Ещё немного — и у соперника появится новый ферзь.' },
    { t: 'Проходную соперника уже не догнать — она идёт в ферзи.', stages: [5, 5] },
  ],

  // the advice (mover = its piece, the king too) was clearly stronger; never «лучший ход», never «без потерь»
  'v3.mistake.slower': [
    { t: 'Можно и так, но ход {конём} был посильнее.' },
    { t: 'Неплохо, но совет пойти {конём} давал больше.' },
    { t: 'Так тоже можно, хотя {конём} вышло бы лучше.' },
    { t: 'Ход нормальный, но {конь} {p:мог|могла} сделать больше.' },
    { t: 'Это не беда, но ход {конём} был заметно сильнее.' },
    { t: 'Годится, только ход {конём} принёс бы больше пользы.' },
    { t: 'Интересная мысль, но я бы пошёл {конём}.' },
    { t: 'А у {коня:gen} был ход ещё сильнее.' },
    { t: 'Сильнее было пойти {конём}.' },
    { t: 'Я бы выбрал ход {конём} — в нём больше силы.' },
  ],

  // ───────────────────────── the rule to remember (first time in a game) ─────────────────────────

  'v3.rule.mate': [
    { t: 'Перед ходом посмотри: нет ли у соперника мата?', stages: Y },
    { t: 'Сначала глянь на своего короля: он в безопасности?', stages: Y },
    { t: 'Правило: сперва думаем за соперника, потом за себя.', stages: Y },
    { t: 'Прежде чем ходить, проверь все шахи соперника.', stages: Y },
    { t: 'Король — самый главный: перед ходом проверь, не грозит ли ему мат.', stages: Y },
    { t: 'Береги короля раньше, чем охотишься за фигурами.', stages: Y },
    { t: 'Каждый ход начинай с вопроса: что задумал соперник?', stages: O },
    { t: 'Сначала шахи соперника — нет ли среди них мата?', stages: O },
    { t: 'Прежде своих планов проверь угрозы соперника, особенно мат.', stages: O },
    { t: 'Правило сильных игроков: сначала безопасность короля, потом атака.', stages: O },
    { t: 'Представь, что ходит соперник: чем он ответит на наш ход?', stages: O },
    { t: 'Мат важнее всего остального — ищи его сначала у соперника.', stages: O },
    { t: 'Сначала смотри, что задумал соперник, — нет ли у него мата.' },
    { t: 'Перед каждым ходом проверь: безопасно ли королю?' },
    { t: 'Чужой мат страшнее своей атаки — проверяй его первым.' },
  ],

  'v3.rule.backRank': [
    { t: 'Дай королю форточку: пусть одна пешка перед ним шагнёт вперёд.' },
    { t: 'Форточка спасает короля от ловушки на последнем ряду.' },
    { t: 'Не запирай короля пешками — оставь ему выход.' },
    { t: 'Домику короля нужно окошко — одна сдвинутая пешка.' },
    { t: 'После рокировки подумай о форточке для короля.' },
    { t: 'Пешки перед королём — стена, но в стене нужна дверца.' },
    { t: 'Следи за последней горизонталью: королю нужна форточка.', stages: [4, 5] },
  ],

  'v3.rule.fork': [
    { t: 'Проверяй, куда может прыгнуть конь соперника и на что он нападёт.' },
    { t: 'Перед ходом посмотри: может ли одна фигура соперника напасть на две наши?' },
    { t: 'Конь соперника любит двойные удары — следи, куда он прыгает.' },
    { t: 'Не ставь две фигуры туда, где их достанет один ход соперника.' },
    { t: 'Смотри, где конь соперника окажется через ход.' },
    { t: 'Фигуры без защиты — любимая цель для двойного удара.' },
    { t: 'Берегись вилок: проверяй прыжки коня соперника.', stages: [2, 5] },
    { t: 'Вилку легче предупредить, чем от неё спастись.', stages: O },
  ],

  'v3.rule.pin': [
    { t: 'Не ставь фигуру на одну линию со своим королём или ферзём.' },
    { t: 'Смотри, куда целятся слоны, ладьи и ферзь соперника.' },
    { t: 'Если за фигурой стоит король, ей нельзя уйти — следи за линиями.' },
    { t: 'Короля и ферзя держи подальше от дальнобойных фигур соперника.' },
    { t: 'Перед ходом проверь: не встанет ли фигура на линию к нашему королю?' },
    { t: 'Короля лучше не ставить на одну линию с ферзём.' },
    { t: 'Связанная фигура почти не может помочь — береги фигуры от связок.', stages: O },
    { t: 'Против связки есть приём: заранее уводи короля с опасной линии.', stages: O },
  ],

  // for the opponent's tactics (skewer, discovered attack, removing a defender, a trapped piece — core RULE_OF)
  'v3.rule.tactic': [
    { t: 'Перед ходом посмотри на каждую фигуру соперника: куда она может пойти?' },
    { t: 'Проверяй не только, кто нападает сейчас, но и кто нападёт после хода.' },
    { t: 'Думай на ход вперёд: как ответит соперник?' },
    // stages 1–2: only a trapped piece leads here (the other tactics are stage-3+ concepts)
    { t: 'Перед ходом проверь: сможет ли фигура потом уйти назад?', stages: Y },
    { t: 'Не заводи фигуру туда, откуда нет дороги обратно.', stages: Y },
    { t: 'У каждой фигуры должен быть запасной выход.', stages: Y },
    { t: 'Смотри на линии: что откроется, если фигура уйдёт?', stages: O },
    { t: 'Фигуры соперника могут прятаться друг за другом — смотри вдоль линий.', stages: O },
    { t: 'Слон, ладья и ферзь бьют издалека — следи за их линиями.', stages: O },
  ],

  // for every hanging case (undefended, cheaper attacker, outnumbered)
  'v3.rule.hanging': [
    { t: 'Перед ходом проверь: все ли наши фигуры защищены?', stages: Y },
    { t: 'Правило зоркого глаза: кого могут съесть после нашего хода?', stages: Y },
    { t: 'Каждой фигуре нужен друг, который её защищает.', stages: Y },
    { t: 'Прежде чем ходить, посмотри, что у нас под ударом.', stages: Y },
    { t: 'Фигура без защиты — подарок сопернику, а подарки мы не раздаём!', stages: Y },
    { t: 'Посчитай, кто нападает на фигуру и кто её защищает.', stages: Y },
    { t: 'Уходишь фигурой — проверь, кого она охраняла.', stages: Y },
    { t: 'Представь себя соперником: кого из наших можно съесть?', stages: Y },
    { t: 'Перед каждым ходом ищи, что висит у нас и у соперника.', stages: O },
    { t: 'Если на фигуру нападает кто-то дешевле, одной защиты мало.', stages: O },
    { t: 'Посчитай: сколько фигур нападает и сколько защищает.', stages: O },
    { t: 'Уходя с места, фигура может оставить соседа без защиты — проверь это.', stages: O },
    { t: 'Правило безопасности: у каждой фигуры должна быть охрана.', stages: O },
    { t: 'Все взятия соперника стоит проверить до своего хода.', stages: O },
    { t: 'Защищённая фигура спокойна, незащищённая — мишень.', stages: O },
    { t: 'Каждый ход задавай два вопроса: что под ударом и чем это защищено?', stages: O },
    { t: 'Перед ходом проверяй: всё ли защищено и кого можно съесть.' },
    { t: 'Сначала безопасность: все ли фигуры прикрыты?' },
    { t: 'Не оставляй фигуры без присмотра.' },
    { t: 'Если нападают двое, а защищает один, фигура в опасности.' },
  ],

  'v3.rule.badTrade': [
    { t: 'Считай: что отдаём и что получаем.' },
    { t: 'Перед разменом сравни, какая фигура дороже.' },
    { t: 'Меняйся, только если получаешь не меньше, чем отдаёшь.' },
    { t: 'Дорогую фигуру на дешёвую не меняют.' },
    { t: 'Цена фигур: пешка — одна, конь и слон — три, ладья — пять, ферзь — девять.' },
    { t: 'Перед взятием посчитай, кто заберёт последним.' },
    { t: 'Бей дешёвой фигурой дорогую, а не наоборот.' },
  ],

  'v3.rule.ignoredDanger': [
    { t: 'Если на фигуру напали, сначала спасаем её.', stages: Y },
    { t: 'Когда фигура в опасности, первым делом уведи или защити её.', stages: Y },
    { t: 'Фигура под ударом ждёт помощи — спешим к ней!', stages: Y },
    { t: 'Правило: сначала помогаем фигуре в беде, потом всё остальное.', stages: Y },
    { t: 'Напали на нашу фигуру — сперва выручаем её.', stages: Y },
    { t: 'Сначала ответь на угрозу, а потом продолжай свой план.', stages: O },
    { t: 'Угрозу нельзя оставлять без ответа: уведи, защити или закрой.', stages: O },
    { t: 'Есть угроза — ищи ход, который её снимает.', stages: O },
    { t: 'Своя атака подождёт, пока наша фигура под ударом.', stages: O },
    { t: 'Перед своим планом убедись, что все угрозы отбиты.', stages: O },
    { t: 'Сначала спасаем того, кого атакуют.' },
    { t: 'Когда соперник нападает, ищем спасение раньше всего.' },
    { t: 'Видишь угрозу — спеши на помощь своей фигуре.' },
    { t: 'Прежде чем нападать, спаси того, кто под ударом.' },
  ],

  'v3.rule.missedTreasure': [
    { t: 'Перед ходом поищи подарки: что можно съесть бесплатно?', stages: Y },
    { t: 'Будь охотником за подарками — смотри, что никто не охраняет.', stages: Y },
    { t: 'Сначала посмотри, что можно съесть, — вдруг там подарок.', stages: Y },
    { t: 'Глянь на каждую фигуру соперника: её кто-нибудь защищает?', stages: Y },
    { t: 'Ищи подарки каждый ход — они прячутся на виду.', stages: Y },
    { t: 'Перед ходом проверь все взятия — вдруг одно из них выгодное.', stages: O },
    { t: 'Шахи, взятия, угрозы — сначала ищи их у себя.', stages: O },
    { t: 'Прежде чем строить план, проверь, нет ли выгодного взятия.', stages: O },
    { t: 'Хороший игрок сначала ищет, что можно выгодно забрать.', stages: O },
    { t: 'Каждая фигура соперника без защиты — повод посчитать взятие.', stages: O },
    { t: 'Не проходи мимо подарков: смотри, что стоит без защиты.' },
    { t: 'Зоркий глаз сначала ищет подарки.' },
    { t: 'Выгодное взятие — первое, что стоит поискать.' },
    { t: 'Прежде своего плана посмотри, что соперник оставил без защиты.' },
  ],

  'v3.rule.earlyQueen': [
    { t: 'Сначала кони и слоны, ферзь — потом.' },
    { t: 'Кони и слоны выходят первыми, а ферзь пусть подождёт.' },
    { t: 'Ферзь — самая сильная фигура, её не пускают вперёд первой.' },
    { t: 'Выведешь ферзя рано — соперник будет его гонять.' },
    { t: 'Пусть ферзь посидит дома, пока выходят другие фигуры.' },
    { t: 'Ферзю нужна компания: выводи его после коней и слонов.' },
    { t: 'Ранний ферзь только бегает, а другие фигуры спят.', stages: [2, 5] },
  ],

  'v3.rule.promotion': [
    { t: 'Чужую пешку, которая бежит в ферзи, останавливай заранее.' },
    { t: 'Если пешке соперника никто не мешает, поставь перед ней фигуру.' },
    { t: 'Следи за пешками соперника: им нельзя давать дойти до конца.' },
    { t: 'Пешку удобно остановить фигурой прямо перед ней.' },
    { t: 'Чем дальше пешка соперника, тем она опаснее — лови её раньше.' },
    { t: 'Король тоже умеет ловить пешки — веди его навстречу.' },
    { t: 'Чужую проходную останавливают как можно раньше.', stages: [5, 5] },
  ],

  // ───────────────────────── the same rule as a question (second time, stages 3–5) ─────────────────────────

  'v3.rule.ask.mate': [
    { t: 'Какие шахи есть у соперника — нет ли среди них мата?' },
    { t: 'Откуда может прийти мат нашему королю?' },
    { t: 'Безопасно ли сейчас нашему королю?' },
    { t: 'Что соперник хочет сделать с нашим королём?' },
    { t: 'Как защитить короля от мата?' },
  ],

  'v3.rule.ask.backRank': [
    { t: 'Есть ли у нашего короля форточка?' },
    { t: 'Куда уйдёт король, если шах придёт по последнему ряду?' },
    { t: 'Кто охраняет последний ряд?' },
    { t: 'Что нужно королю, чтобы не попасть в ловушку на последнем ряду?' },
    { t: 'Какую пешку сдвинуть, чтобы у короля появилась форточка?' },
  ],

  'v3.rule.ask.fork': [
    { t: 'Куда может прыгнуть конь соперника и на что он нападёт?' },
    { t: 'Может ли одна фигура соперника напасть на две наши?' },
    { t: 'Какие две наши фигуры можно атаковать одним ходом?' },
    { t: 'Где соперник может сделать вилку?' },
    { t: 'Как спрятать фигуры от двойного удара?' },
  ],

  'v3.rule.ask.pin': [
    { t: 'Какая наша фигура стоит на одной линии с королём или ферзём?' },
    { t: 'Может ли наша фигура уйти, если за ней стоит король?' },
    { t: 'Какую нашу фигуру соперник может связать?' },
    { t: 'Куда целятся дальнобойные фигуры соперника?' },
    { t: 'Что прячется за нашей фигурой на линии?' },
  ],

  'v3.rule.ask.tactic': [
    { t: 'Что откроется, если фигура уйдёт с линии?' },
    { t: 'Какие линии сейчас опасны для нас?' },
    { t: 'Кто из фигур соперника смотрит вдоль линии на наши фигуры?' },
    { t: 'Какая фигура соперника прячется за другой?' },
    { t: 'Что соперник сможет сделать после нашего хода?' },
  ],

  'v3.rule.ask.hanging': [
    { t: 'Что теперь без защиты?' },
    { t: 'Все ли наши фигуры защищены?' },
    { t: 'Кто нападает на нашу фигуру и кто её защищает?' },
    { t: 'Какая наша фигура сейчас в опасности?' },
    { t: 'Кого из наших можно съесть бесплатно?' },
  ],

  'v3.rule.ask.badTrade': [
    { t: 'Что мы отдаём и что получаем?' },
    { t: 'Какая фигура дороже: наша или та, которую мы берём?' },
    { t: 'Кто заберёт последним в этом размене?' },
    { t: 'Выгоден ли нам такой размен?' },
    { t: 'Сколько стоит наша фигура и сколько — фигура соперника?' },
  ],

  'v3.rule.ask.ignoredDanger': [
    { t: 'Кого сейчас атакует соперник?' },
    { t: 'Какая наша фигура под ударом — и как её спасти?' },
    { t: 'Что важнее сейчас: наш план или угроза?' },
    { t: 'Кого надо спасать первым?' },
    { t: 'Как убрать фигуру из-под удара?' },
  ],

  'v3.rule.ask.missedTreasure': [
    { t: 'Нет ли на доске бесплатного угощения?' },
    { t: 'Есть ли у соперника фигура без защиты?' },
    { t: 'Какие взятия у нас есть — нет ли среди них подарка?' },
    { t: 'Какую фигуру соперника можно забрать с выгодой?' },
    { t: 'Где соперник оставил подарок?' },
  ],

  'v3.rule.ask.earlyQueen': [
    { t: 'Кто должен выходить раньше ферзя?' },
    { t: 'Какие фигуры выводим до ферзя?' },
    { t: 'Что будет с ферзём, если он выйдет слишком рано?' },
    { t: 'Почему ферзю пока лучше подождать дома?' },
    { t: 'Кто будет гонять нашего ферзя?' },
  ],

  'v3.rule.ask.promotion': [
    { t: 'Какая пешка соперника бежит в ферзи?' },
    { t: 'Кто остановит пешку соперника?' },
    { t: 'Далеко ли пешке соперника до превращения?' },
    { t: 'Какая фигура встанет перед пешкой соперника?' },
    { t: 'Успеет ли наша фигура догнать пешку соперника?' },
    { t: 'Как задержать чужую проходную?', stages: [5, 5] },
  ],

  // ───────────────────────── the take-back offer and the replies ─────────────────────────

  // the opener of the offer (a foal's «тпру» is his own)
  'v3.takeback.stop': [
    { t: 'Стоп-стоп!' },
    { t: 'Подожди-ка!' },
    { t: 'Ой-ой, погоди!' },
    { t: 'Тпру, постой!' },
    { t: 'Стой-стой-стой!' },
    { t: 'Ну-ка, погоди!' },
    { t: 'Ой, стоп!' },
    { t: 'Постой-ка!' },
    { t: 'Так-так, подожди!' },
    { t: 'Ай, стоп-стоп!' },
    { t: 'Притормози-ка!' },
    { t: 'Ух, подожди!' },
  ],

  // after «what happened»: the yes/no question
  'v3.takeback.ask': [
    { t: 'Вернём ход?' },
    { t: 'Вернём ход и подумаем ещё?' },
    { t: 'Может, вернём ход?' },
    { t: 'Хочешь вернуть ход?' },
    { t: 'Давай вернём ход и поищем другой?' },
    { t: 'Попробуем другой ход?' },
    { t: 'Сыграем по-другому?' },
    { t: 'Отменим ход и подумаем вместе?' },
    { t: 'Поищем ход понадёжнее?' },
    { t: 'Возьмём ход назад?' },
    { t: 'Отмотаем ход назад?' },
    { t: 'Переиграем этот ход?' },
  ],

  // stages 3–5, after «Стоп!»: a question instead of the explanation (the highlight comes after «Верну»);
  // any concept but «slower» can stand behind it, so the questions stay general
  'v3.takeback.askThink': [
    { t: 'Что теперь может съесть соперник?' },
    { t: 'Как теперь ответит соперник?' },
    { t: 'Посмотри глазами соперника: какой у него теперь ход?' },
    { t: 'Если бы сейчас ходил соперник, что бы он сделал?' },
    { t: 'Чем соперник теперь может нас огорчить?' },
    { t: 'Кого из наших соперник теперь может обидеть?' },
    { t: 'Что изменилось на доске после нашего хода?' },
    { t: 'Проверим вместе: чем опасен этот ход?' },
  ],

  // the child took the move back: approve the decision to think, not the child (no «молодец», no «так держать»);
  // stages 1–2: said alone, the advice follows; stages 3–5: a mistake line follows
  'v3.takeback.yes': [
    { t: 'Хорошее решение — подумать ещё раз.' },
    { t: 'Правильно: не торопимся и смотрим внимательно.' },
    { t: 'Остановиться и проверить — привычка сильных игроков.' },
    { t: 'Спокойствие — половина победы.' },
    { t: 'Ход вернули — теперь спокойно ищем другой.' },
    { t: 'Здорово, что не спешим!' },
    { t: 'Договорились — посмотрим на доску свежим глазом.' },
    { t: 'Подумать ещё разок — это по-чемпионски.' },
    { t: 'Сначала думаем, потом ходим — наше золотое правило!' },
    { t: 'Проверить себя никогда не лишнее.' },
    { t: 'Вот теперь найдём ход получше.' },
    { t: 'Мне нравится, как мы думаем вместе.' },
    { t: 'Вовремя остановились — это главное!' },
    { t: 'Фигура вернулась на место — выберем ход с умом.' },
    { t: 'Я и сам люблю лишний раз проверить ход.' },
    { t: 'Шахматы любят терпеливых!' },
    { t: 'Отмотали назад — глядим в оба!' },
    { t: 'Вторая попытка — самая интересная!' },
  ],

  // the child kept the move: calm, no reproach, play on; stages 3–5: a mistake line follows
  'v3.takeback.no': [
    { t: 'Хорошо, играем дальше.' },
    { t: 'Договорились, оставляем твой ход.' },
    { t: 'Твой выбор — продолжаем!' },
    { t: 'Ладно, узнаем, что из этого выйдет.' },
    { t: 'Идём вперёд — я рядом, если что.' },
    { t: 'Так и сыграем.' },
    { t: 'Ничего, партия продолжается!' },
    { t: 'Ход остаётся — поехали!' },
    { t: 'Оставляем как есть и смотрим, что ответит соперник.' },
    { t: 'Решено — сражаемся!' },
    { t: 'Смелое решение — посмотрим, как сложится.' },
    { t: 'Как скажешь — партия твоя.' },
    { t: 'Принято, капитан!' },
    { t: 'Что бы ни случилось, разберёмся вместе.' },
    { t: 'Ну что ж, скачем дальше!' },
    { t: 'Понял тебя — играем!' },
  ],

  // after the take-back the child played another move that loses too (no «опять», no «снова»): a statement that
  // opens the second offer (stages 1–2: + a mistake line + «Вернём ход?»; stages 3–5: + an `askThink` question)
  'v3.takeback.again': [
    { t: 'Хм, и этот ход что-то теряет.' },
    { t: 'И так тоже опасно.' },
    { t: 'Ой, и здесь есть подвох.' },
    { t: 'И этот путь не годится — поищем ещё.' },
    { t: 'Хм, и тут у соперника найдётся ответ.' },
    { t: 'Этот вариант тоже не спасает.' },
    { t: 'Копытом чую: и здесь что-то не так.' },
    { t: 'И у этого хода есть слабое место.' },
    { t: 'Ох, этот ход тоже на руку сопернику.' },
    { t: 'Похоже, и такой ход сопернику по вкусу.' },
    { t: 'Тпру, и тут соперник может нас подловить.' },
    { t: 'Хитрый соперник и здесь нашёл бы лазейку.' },
    { t: 'Ой-ой, и такой ход открывает сопернику дорожку.' },
    { t: 'Погоди-ка, и так не выйдет.' },
    { t: 'И тут в нашей обороне дырочка.' },
    { t: 'А соперник и тут начеку.' },
  ],
};
